/**
 * React hook for real-time memory updates via Server-Sent Events.
 * The browser EventSource API hides HTTP status and reconnects with the same
 * URL, so this hook uses a fetch stream that can refresh an expired token.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Memory, apiService } from "../services/api";
import {
  refreshStreamAccessToken,
  runMemoryEventStream,
} from "../services/memoryEventStream";

interface MemoryStreamEvent {
  type:
    | "connected"
    | "heartbeat"
    | "memory_created"
    | "memory_updated"
    | "memory_deleted";
  data?: any;
  timestamp: string;
  space_id?: string;
}

interface UseMemoryStreamOptions {
  spaceId?: string;
  enabled?: boolean;
  onMemoryCreated?: (memory: Memory) => void;
  onMemoryUpdated?: (memory: Memory) => void;
  onMemoryDeleted?: (memoryId: string) => void;
  onConnectionChange?: (connected: boolean) => void;
  onSacredEvent?: (eventType: string, data?: any) => void;
}

interface UseMemoryStreamReturn {
  isConnected: boolean;
  isReconnecting: boolean;
  lastEvent: MemoryStreamEvent | null;
  connectionQuality: "excellent" | "good" | "poor" | "severed";
  reconnect: () => void;
  disconnect: () => void;
}

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
const HEARTBEAT_TIMEOUT_MS = 45000;

export function useMemoryStream(
  options: UseMemoryStreamOptions = {},
): UseMemoryStreamReturn {
  const { spaceId, enabled = true } = options;
  const { getAccessToken, logout } = useAuth();

  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [lastEvent, setLastEvent] = useState<MemoryStreamEvent | null>(null);
  const [connectionQuality, setConnectionQuality] = useState<
    "excellent" | "good" | "poor" | "severed"
  >("severed");
  const [connectionEpoch, setConnectionEpoch] = useState(0);

  const optionsRef = useRef(options);
  optionsRef.current = options;
  const authRef = useRef({ getAccessToken, logout });
  authRef.current = { getAccessToken, logout };
  const connectedRef = useRef(false);
  const heartbeatTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const manualStopRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const reconnectRef = useRef<() => void>(() => {});

  const clearHeartbeat = useCallback(() => {
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  }, []);

  const resetHeartbeat = useCallback(() => {
    clearHeartbeat();
    heartbeatTimeoutRef.current = setTimeout(() => {
      if (connectedRef.current) {
        console.warn("Memory stream heartbeat missed");
        reconnectRef.current();
      }
    }, HEARTBEAT_TIMEOUT_MS);
  }, [clearHeartbeat]);

  const emitQuality = useCallback(
    (quality: "excellent" | "good" | "poor" | "severed") => {
      setConnectionQuality(quality);
      optionsRef.current.onSacredEvent?.("connection_quality_change", {
        quality,
      });
    },
    [],
  );

  const disconnect = useCallback(() => {
    manualStopRef.current = true;
    abortRef.current?.abort();
    clearHeartbeat();
    connectedRef.current = false;
    setIsConnected(false);
    setIsReconnecting(false);
    emitQuality("severed");
    optionsRef.current.onConnectionChange?.(false);
    optionsRef.current.onSacredEvent?.("connection_closed", {
      message: "Sacred portal closed peacefully",
    });
    setConnectionEpoch((epoch) => epoch + 1);
  }, [clearHeartbeat, emitQuality]);

  const reconnect = useCallback(() => {
    manualStopRef.current = false;
    abortRef.current?.abort();
    setConnectionEpoch((epoch) => epoch + 1);
  }, []);

  reconnectRef.current = reconnect;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    // disconnect() sets this flag and bumps the epoch. Consume it so a later
    // space change can connect again, while this run stays stopped.
    if (manualStopRef.current) {
      manualStopRef.current = false;
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    let active = true;

    void runMemoryEventStream({
      apiBaseUrl: API_BASE_URL,
      spaceId,
      signal: controller.signal,
      shouldContinue: () => active && !manualStopRef.current,
      getAccessToken: () => authRef.current.getAccessToken(),
      refreshAccessToken: async (previousToken) => {
        const refreshed = await refreshStreamAccessToken(previousToken);
        if (refreshed.status === "refreshed") {
          apiService.setAuthToken(refreshed.token);
        }
        return refreshed;
      },
      signOut: () => authRef.current.logout(),
      onOpen: () => {
        if (!active) {
          return;
        }
        connectedRef.current = true;
        setIsConnected(true);
        setIsReconnecting(false);
        emitQuality("excellent");
        optionsRef.current.onConnectionChange?.(true);
        optionsRef.current.onSacredEvent?.("connection_established", {
          message: "Sacred memory stream awakened",
          spaceId,
        });
        resetHeartbeat();
      },
      onDown: () => {
        if (!active) {
          return;
        }
        connectedRef.current = false;
        clearHeartbeat();
        setIsConnected(false);
        emitQuality("severed");
        optionsRef.current.onConnectionChange?.(false);
      },
      onReconnecting: ({ attempt, delayMs }) => {
        if (!active) {
          return;
        }
        setIsReconnecting(true);
        emitQuality("poor");
        optionsRef.current.onSacredEvent?.("reconnection_attempt", {
          attempt,
          delay: delayMs,
          message: "Attempting to restore sacred connection",
        });
      },
      onGaveUp: () => {
        if (!active) {
          return;
        }
        setIsReconnecting(false);
        emitQuality("severed");
        optionsRef.current.onSacredEvent?.("connection_abandoned", {
          message: "Sacred connection permanently severed",
        });
      },
      onMessage: (raw) => {
        if (!active) {
          return;
        }
        let parsedEvent: MemoryStreamEvent;
        try {
          parsedEvent = JSON.parse(raw) as MemoryStreamEvent;
        } catch (error) {
          emitQuality("poor");
          optionsRef.current.onSacredEvent?.("parsing_error", {
            error: error instanceof Error ? error.message : String(error),
            message: "Sacred message corrupted",
          });
          return;
        }

        setLastEvent(parsedEvent);
        const handlers = optionsRef.current;
        switch (parsedEvent.type) {
          case "connected":
            emitQuality("excellent");
            handlers.onSacredEvent?.("handshake_complete", {
              message: "Sacred connection blessed",
            });
            break;
          case "heartbeat":
            emitQuality("excellent");
            resetHeartbeat();
            break;
          case "memory_created":
            if (parsedEvent.data && handlers.onMemoryCreated) {
              handlers.onMemoryCreated(parsedEvent.data);
              handlers.onSacredEvent?.("memory_manifested", {
                memory: parsedEvent.data,
                message: "New wisdom captured in sanctuary",
              });
            }
            break;
          case "memory_updated":
            if (parsedEvent.data && handlers.onMemoryUpdated) {
              handlers.onMemoryUpdated(parsedEvent.data);
              handlers.onSacredEvent?.("memory_enhanced", {
                memory: parsedEvent.data,
                message: "Wisdom polished with sacred insight",
              });
            }
            break;
          case "memory_deleted":
            if (parsedEvent.data?.id && handlers.onMemoryDeleted) {
              handlers.onMemoryDeleted(parsedEvent.data.id);
              handlers.onSacredEvent?.("memory_released", {
                memoryId: parsedEvent.data.id,
                message: "Memory peacefully released",
              });
            }
            break;
          default:
            handlers.onSacredEvent?.("unknown_event", {
              type: parsedEvent.type,
              data: parsedEvent.data,
            });
        }
      },
    });

    return () => {
      active = false;
      connectedRef.current = false;
      controller.abort();
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      clearHeartbeat();
      setIsConnected(false);
      setIsReconnecting(false);
    };
  }, [
    clearHeartbeat,
    connectionEpoch,
    emitQuality,
    enabled,
    resetHeartbeat,
    spaceId,
  ]);

  return {
    isConnected,
    isReconnecting,
    lastEvent,
    connectionQuality,
    reconnect,
    disconnect,
  };
}
